// src/pages/user/DepositHistory.js
import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { CheckCircle, XCircle, Clock, DollarSign, Gift, ChevronDown, ChevronUp, Wallet, History, Calendar, CreditCard, Package, TrendingUp } from 'lucide-react';

const DepositHistory = () => {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [expandedDeposit, setExpandedDeposit] = useState(null);
  const [userFundBalance, setUserFundBalance] = useState(0);
  const [userAddedFund, setUserAddedFund] = useState(0);
  const [userFundCount, setUserFundCount] = useState(0);

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const storedUserName = localStorage.getItem('userName');
    const storedDbKey = localStorage.getItem('dbKey') || storedUserId;
    
    if (storedUserId) {
      setUserId(storedUserId);
      setUserName(storedUserName);
      fetchDeposits(storedUserId);
      fetchUserFundData(storedDbKey);
    }
  }, []);

  // Fetch user fund data
  const fetchUserFundData = async (dbKey) => {
    try {
      const userRef = ref(database, `users/${dbKey}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userData = snapshot.val();
        const fundData = userData.fund || {};
        
        // Get fund data from fund object
        const currentFundBalance = parseFloat(fundData.balance) || 0;
        const currentAddedFund = parseFloat(fundData.total) || 0;
        const currentFundCount = parseInt(fundData.count) || 0;
        
        // Update state
        setUserFundBalance(currentFundBalance);
        setUserAddedFund(currentAddedFund);
        setUserFundCount(currentFundCount);
      }
    } catch (error) {
      console.error('Error fetching user fund data:', error);
    }
  };

  const fetchDeposits = async (userId) => {
    try {
      const depositsRef = ref(database, 'Deposits');
      const snapshot = await get(depositsRef);
      
      if (snapshot.exists()) {
        const depositsData = snapshot.val();
        const userDeposits = Object.keys(depositsData)
          .map(key => ({ id: key, ...depositsData[key] }))
          .filter(deposit => deposit.userId === userId)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        console.log('User deposits:', userDeposits);
        setDeposits(userDeposits);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching deposits:', error);
      setLoading(false);
    }
  };

  const toggleDepositDetails = (depositId) => {
    setExpandedDeposit(expandedDeposit === depositId ? null : depositId);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'rejected': return <XCircle className="h-5 w-5 text-red-400" />;
      default: return <Clock className="h-5 w-5 text-yellow-400" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  // Calculate totals
  const calculateTotals = () => {
    const totals = {
      totalDeposits: 0,
      totalBonus: 0,
      totalAddedToFund: 0,
      approvedCount: 0,
      pendingCount: 0
    };

    deposits.forEach(deposit => {
      if (deposit.status === 'approved') {
        totals.totalDeposits += parseFloat(deposit.amount) || 0;
        totals.totalBonus += parseFloat(deposit.welcomeBonus || 0);
        totals.totalAddedToFund += (parseFloat(deposit.amount) || 0) + (parseFloat(deposit.welcomeBonus || 0));
        totals.approvedCount++;
      } else if (deposit.status === 'pending') {
        totals.pendingCount++;
      }
    });

    return totals;
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto pt-4 md:pt-20 px-2 md:px-4">
        <div className="flex justify-center items-center h-40 md:h-64">
          <div className="animate-spin rounded-full h-10 md:h-12 w-10 md:w-12 border-t-2 border-b-2 border-gold-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pt-4 md:pt-20 px-2 md:px-4">
      {/* Header */}
      <div className="mb-4 md:mb-8">
        <h1 className="text-xl md:text-3xl font-bold text-gold-100">Deposit History</h1>
        <p className="text-gold-400 text-sm md:text-base mt-1 md:mt-2">
          User: <span className="text-gold-200 font-semibold">{userName}</span>
          <span className="mx-2 hidden md:inline">•</span>
          <span className="block md:inline mt-1 md:mt-0">
            ID: <span className="text-gold-200 font-semibold">{userId}</span>
          </span>
        </p>
      </div>

      {/* Fund Stats */}
      <div className="mb-4 md:mb-8 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-gradient-to-br from-green-900/40 to-black border border-green-500/50 rounded-xl md:rounded-2xl p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <Wallet className="text-2xl md:text-3xl text-green-400" />
              <div>
                <h3 className="text-base md:text-xl font-bold text-green-100">Fund Balance</h3>
                <p className="text-green-400 text-xs md:text-sm">Current Balance</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl md:text-3xl font-bold text-green-200">${userFundBalance.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-900/40 to-black border border-blue-500/50 rounded-xl md:rounded-2xl p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <History className="text-2xl md:text-3xl text-blue-400" />
              <div>
                <h3 className="text-base md:text-xl font-bold text-blue-100">User Added Fund</h3>
                <p className="text-blue-400 text-xs md:text-sm">Total deposits (without bonus)</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl md:text-3xl font-bold text-blue-200">${userAddedFund.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-900/40 to-black border border-purple-500/50 rounded-xl md:rounded-2xl p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <Wallet className="text-2xl md:text-3xl text-purple-400" />
              <div>
                <h3 className="text-base md:text-xl font-bold text-purple-100">Fund Count</h3>
                <p className="text-purple-400 text-xs md:text-sm">Total transactions</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl md:text-3xl font-bold text-purple-200">{userFundCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Deposit Summary Stats */}
      <div className="mb-4 md:mb-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-gold-900/40 border border-gold-600/50 rounded-xl p-4">
          <div className="text-center">
            <div className="text-gold-400 text-sm mb-1">Total Deposits</div>
            <div className="text-2xl font-bold text-gold-200">{deposits.length}</div>
          </div>
        </div>
        <div className="bg-green-900/40 border border-green-600/50 rounded-xl p-4">
          <div className="text-center">
            <div className="text-green-400 text-sm mb-1">Total Deposit Amount</div>
            <div className="text-2xl font-bold text-green-200">
              ${totals.totalDeposits.toFixed(2)}
            </div>
          </div>
        </div>
        <div className="bg-blue-900/40 border border-blue-600/50 rounded-xl p-4">
          <div className="text-center">
            <div className="text-blue-400 text-sm mb-1">Total Bonus</div>
            <div className="text-2xl font-bold text-blue-200">
              ${totals.totalBonus.toFixed(2)}
            </div>
          </div>
        </div>
        <div className="bg-purple-900/40 border border-purple-600/50 rounded-xl p-4">
          <div className="text-center">
            <div className="text-purple-400 text-sm mb-1">Approved</div>
            <div className="text-2xl font-bold text-purple-200">
              {totals.approvedCount}
            </div>
          </div>
        </div>
      </div>
      
      {deposits.length === 0 ? (
        <div className="text-center py-10 md:py-16 bg-gold-900/30 border border-gold-600/50 rounded-xl md:rounded-2xl">
          <div className="text-5xl md:text-6xl mb-3 md:mb-4">💰</div>
          <h3 className="text-lg md:text-xl font-semibold text-gold-200 mb-1 md:mb-2">No Deposit History</h3>
          <p className="text-gold-400 text-sm md:text-base">You haven't made any deposits yet.</p>
        </div>
      ) : (
        <div className="space-y-3 md:space-y-4">
          {/* Mobile Deposit List */}
          <div className="md:hidden space-y-3">
            {deposits.map((deposit) => {
              const totalAmount = parseFloat(deposit.amount) + parseFloat(deposit.welcomeBonus || 0);
              
              return (
                <div key={deposit.id} className="bg-gold-900/30 border border-gold-600/50 rounded-xl p-4">
                  {/* Mobile Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(deposit.status)}
                      <div>
                        <h3 className="text-base font-bold text-gold-100">
                          Deposit #{deposit.depositId?.substring(0, 8)}...
                        </h3>
                        <p className="text-gold-400 text-xs">
                          {formatShortDate(deposit.createdAt)} • {formatTime(deposit.createdAt)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleDepositDetails(deposit.id)}
                      className="text-gold-400 hover:text-gold-200"
                    >
                      {expandedDeposit === deposit.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  </div>
                  
                  {/* Mobile Quick Stats */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-black/40 rounded-lg p-2 text-center">
                      <div className="text-gold-400 text-xs">Deposit</div>
                      <div className="text-sm font-bold text-gold-200">${parseFloat(deposit.amount).toFixed(2)}</div>
                    </div>
                    
                    <div className="bg-green-900/30 rounded-lg p-2 text-center">
                      <div className="text-green-400 text-xs">Bonus</div>
                      <div className="text-sm font-bold text-green-300">+${parseFloat(deposit.welcomeBonus || 0).toFixed(2)}</div>
                    </div>
                    
                    <div className="bg-blue-900/30 rounded-lg p-2 text-center">
                      <div className="text-blue-400 text-xs">Total</div>
                      <div className="text-base font-bold text-blue-300">${totalAmount.toFixed(2)}</div>
                    </div>
                  </div>
                  
                  {/* Status and Payment Method */}
                  <div className="flex items-center justify-between">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      deposit.status === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                      deposit.status === 'rejected' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                      'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`}>
                      {deposit.status.charAt(0).toUpperCase() + deposit.status.slice(1)}
                    </span>
                    
                    {deposit.paymentMethod && (
                      <span className="text-gold-300 text-xs">
                        {deposit.paymentMethod}
                      </span>
                    )}
                  </div>
                  
                  {/* Mobile Expanded Details */}
                  {expandedDeposit === deposit.id && (
                    <div className="mt-4 pt-4 border-t border-gold-600/30">
                      <div className="space-y-3">
                        <div>
                          <div className="text-gold-400 text-xs mb-1">Deposit ID</div>
                          <div className="text-gold-200 text-sm font-mono break-all">{deposit.depositId}</div>
                        </div>
                        
                        <div>
                          <div className="text-gold-400 text-xs mb-1">Date & Time</div>
                          <div className="text-gold-200 text-sm">{formatDate(deposit.createdAt)}</div>
                          <div className="text-gold-500 text-xs">{formatTimeAgo(deposit.createdAt)}</div>
                        </div>
                        
                        {deposit.packageName && (
                          <div>
                            <div className="text-gold-400 text-xs mb-1">Package</div>
                            <div className="text-gold-200 text-sm">{deposit.packageName}</div>
                          </div>
                        )}
                        
                        {deposit.paymentMethodName && (
                          <div>
                            <div className="text-gold-400 text-xs mb-1">Payment Method</div>
                            <div className="text-gold-200 text-sm">{deposit.paymentMethodName}</div>
                          </div>
                        )}
                        
                        {deposit.previousDepositTotal && deposit.newDepositTotal && (
                          <div className="bg-blue-900/20 rounded-lg p-3">
                            <div className="text-blue-400 text-xs mb-2">Deposit Totals</div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <div className="text-blue-300 text-xs">Previous Total</div>
                                <div className="text-blue-200 text-sm font-bold">${parseFloat(deposit.previousDepositTotal).toFixed(2)}</div>
                              </div>
                              <div>
                                <div className="text-green-400 text-xs">New Total</div>
                                <div className="text-green-300 text-sm font-bold">${parseFloat(deposit.newDepositTotal).toFixed(2)}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {deposit.fundUpdateDetails && (
                          <div className="bg-green-900/20 rounded-lg p-3">
                            <div className="text-green-400 text-xs mb-2">Fund Wallet Update</div>
                            <div className="space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <div className="text-green-300 text-xs">Previous Balance</div>
                                  <div className="text-green-200 text-xs font-bold">${parseFloat(deposit.fundUpdateDetails.previousFundBalance || 0).toFixed(2)}</div>
                                </div>
                                <div>
                                  <div className="text-green-300 text-xs">New Balance</div>
                                  <div className="text-green-200 text-xs font-bold">${parseFloat(deposit.fundUpdateDetails.newFundBalance || 0).toFixed(2)}</div>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <div className="text-blue-300 text-xs">Previous Added</div>
                                  <div className="text-blue-200 text-xs font-bold">${parseFloat(deposit.fundUpdateDetails.previousAddedFund || 0).toFixed(2)}</div>
                                </div>
                                <div>
                                  <div className="text-blue-300 text-xs">New Added</div>
                                  <div className="text-blue-200 text-xs font-bold">${parseFloat(deposit.fundUpdateDetails.newAddedFund || 0).toFixed(2)}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="bg-black/40 rounded-lg p-3">
                          <div className="text-gold-400 text-xs mb-1">Transaction Summary</div>
                          <div className="text-gold-200 text-sm">
                            Deposit: ${parseFloat(deposit.amount).toFixed(2)} + 
                            Bonus: ${parseFloat(deposit.welcomeBonus || 0).toFixed(2)} = 
                            Total: ${totalAmount.toFixed(2)}
                          </div>
                        </div>
                        
                        {deposit.adminNote && (
                          <div className="bg-black/40 rounded-lg p-3">
                            <div className="text-gold-300 text-xs mb-1">Admin Note</div>
                            <div className="text-gold-100 text-sm">{deposit.adminNote}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop Deposit List */}
          <div className="hidden md:block space-y-4">
            {deposits.map((deposit) => {
              const totalAmount = parseFloat(deposit.amount) + parseFloat(deposit.welcomeBonus || 0);
              
              return (
                <div key={deposit.id} className="bg-gold-900/30 border border-gold-600/50 rounded-2xl p-6">
                  <div className="flex items-center justify-between gap-6">
                    {/* Status and Basic Info */}
                    <div className="flex items-center gap-4 min-w-[300px]">
                      {getStatusIcon(deposit.status)}
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gold-100 truncate">
                          Deposit #{deposit.depositId}
                        </h3>
                        <div className="flex items-center gap-2 text-gold-400 text-sm mt-1">
                          <Calendar size={14} />
                          <span>{formatShortDate(deposit.createdAt)}</span>
                          <span>•</span>
                          <Clock size={14} />
                          <span>{formatTime(deposit.createdAt)}</span>
                          <span>•</span>
                          <span>{formatTimeAgo(deposit.createdAt)}</span>
                        </div>
                        {deposit.packageName && (
                          <div className="flex items-center gap-1 mt-1">
                            <Package size={14} className="text-gold-500" />
                            <span className="text-gold-500 text-sm">{deposit.packageName}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Amount Stats */}
                    <div className="grid grid-cols-3 gap-6 flex-1">
                      <div className="text-center">
                        <div className="text-gold-400 text-sm">Deposit Amount</div>
                        <div className="text-xl font-bold text-gold-200">
                          ${parseFloat(deposit.amount).toFixed(2)}
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-gold-400 text-sm">Welcome Bonus</div>
                        <div className="text-lg font-bold text-green-400">
                          +${parseFloat(deposit.welcomeBonus || 0).toFixed(2)}
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-gold-400 text-sm">Total Added</div>
                        <div className="text-2xl font-bold text-green-400">
                          ${totalAmount.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Status Badge */}
                    <div className="text-center min-w-[140px]">
                      <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                        deposit.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                        deposit.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {deposit.status.charAt(0).toUpperCase() + deposit.status.slice(1)}
                      </span>
                      {deposit.paymentMethodName && (
                        <div className="text-gold-300 text-xs mt-1">
                          <CreditCard size={12} className="inline mr-1" />
                          {deposit.paymentMethodName}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Additional Details - Desktop */}
                  <div className="mt-6 grid grid-cols-2 gap-6">
                    {/* Deposit Total Update */}
                    {deposit.previousDepositTotal && deposit.newDepositTotal && (
                      <div className="bg-blue-900/20 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-blue-400 text-sm mb-2">
                          <TrendingUp size={16} />
                          Deposit Totals
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-blue-300 text-xs">Previous</div>
                            <div className="text-blue-200 font-bold">${parseFloat(deposit.previousDepositTotal).toFixed(2)}</div>
                          </div>
                          <div className="text-blue-400">→</div>
                          <div>
                            <div className="text-green-400 text-xs">New Total</div>
                            <div className="text-green-300 font-bold">${parseFloat(deposit.newDepositTotal).toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Fund Wallet Update */}
                    {deposit.fundUpdateDetails && (
                      <div className="bg-green-900/20 rounded-xl p-4">
                        <div className="flex items-center gap-2 text-green-400 text-sm mb-2">
                          <Wallet size={16} />
                          Fund Wallet Update
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-green-300 text-xs">Previous Balance</div>
                            <div className="text-green-200 font-bold">
                              ${parseFloat(deposit.fundUpdateDetails.previousFundBalance || 0).toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <div className="text-green-300 text-xs">New Balance</div>
                            <div className="text-green-200 font-bold">
                              ${parseFloat(deposit.fundUpdateDetails.newFundBalance || 0).toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <div className="text-blue-300 text-xs">Previous Added</div>
                            <div className="text-blue-200 font-bold">
                              ${parseFloat(deposit.fundUpdateDetails.previousAddedFund || 0).toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <div className="text-blue-300 text-xs">New Added</div>
                            <div className="text-blue-200 font-bold">
                              ${parseFloat(deposit.fundUpdateDetails.newAddedFund || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Transaction Details */}
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    {deposit.paymentMethodName && (
                      <div className="bg-black/40 rounded-xl p-4">
                        <div className="text-gold-400 text-sm mb-1">Payment Method</div>
                        <div className="text-gold-100 font-medium flex items-center gap-2">
                          <CreditCard size={16} />
                          {deposit.paymentMethodName}
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-black/40 rounded-xl p-4">
                      <div className="text-gold-400 text-sm mb-1">Transaction Type</div>
                      <div className="text-gold-100 font-medium">Deposit + Welcome Bonus</div>
                    </div>
                  </div>
                  
                  {/* Admin Note */}
                  {deposit.adminNote && (
                    <div className="mt-4 p-4 bg-black/40 rounded-xl">
                      <div className="text-gold-300 text-sm mb-1">Admin Note:</div>
                      <div className="text-gold-100">{deposit.adminNote}</div>
                    </div>
                  )}
                  
                  {/* Expand Button */}
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => toggleDepositDetails(deposit.id)}
                      className="flex items-center gap-1 mx-auto px-4 py-2 bg-black/40 border border-gold-600/50 rounded-lg text-gold-200 hover:bg-gold-800/40 transition text-sm"
                    >
                      {expandedDeposit === deposit.id ? (
                        <>
                          <ChevronUp size={16} />
                          Show Less Details
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Show More Details
                        </>
                      )}
                    </button>
                  </div>
                  
                  {/* Full Details (Expanded) */}
                  {expandedDeposit === deposit.id && (
                    <div className="mt-6 pt-6 border-t border-gold-600/30">
                      <h4 className="text-lg font-bold text-gold-100 mb-4">Transaction Details</h4>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <div>
                            <div className="text-gold-400 text-sm mb-1">Full Deposit ID</div>
                            <div className="text-gold-100 font-mono text-sm break-all">{deposit.depositId}</div>
                          </div>
                          <div>
                            <div className="text-gold-400 text-sm mb-1">Created At</div>
                            <div className="text-gold-100">{formatDate(deposit.createdAt)}</div>
                          </div>
                          <div>
                            <div className="text-gold-400 text-sm mb-1">Approved At</div>
                            <div className="text-gold-100">{formatDate(deposit.approvedAt || deposit.createdAt)}</div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <div className="text-gold-400 text-sm mb-1">Wallet Type</div>
                            <div className="text-gold-100">{deposit.walletType || 'Fund Wallet'}</div>
                          </div>
                          <div>
                            <div className="text-gold-400 text-sm mb-1">Approved By</div>
                            <div className="text-gold-100">{deposit.approvedBy || 'Auto System'}</div>
                          </div>
                          <div>
                            <div className="text-gold-400 text-sm mb-1">Database Reference</div>
                            <div className="text-gold-100 text-sm">Deposits/{deposit.id}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile Summary Footer */}
          {deposits.length > 0 && (
            <div className="md:hidden mt-6 p-4 bg-gold-900/40 border border-gold-600/50 rounded-xl">
              <div className="text-center">
                <div className="text-gold-300 text-sm mb-2">Deposit Summary</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gold-400 text-xs">Total Deposit Amount</div>
                    <div className="text-lg font-bold text-gold-200">
                      ${totals.totalDeposits.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gold-400 text-xs">Total Bonus</div>
                    <div className="text-lg font-bold text-green-400">
                      ${totals.totalBonus.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gold-400 text-xs">Total Added to Fund</div>
                    <div className="text-lg font-bold text-blue-400">
                      ${totals.totalAddedToFund.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gold-400 text-xs">Approved Deposits</div>
                    <div className="text-lg font-bold text-green-400">
                      {totals.approvedCount}/{deposits.length}
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gold-600/30">
                  <div className="text-gold-300 text-sm">
                    Showing <span className="text-gold-100 font-bold">{deposits.length}</span> deposits
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Desktop Summary Footer */}
          {deposits.length > 0 && (
            <div className="hidden md:block mt-8 p-6 bg-gold-900/40 border border-gold-600/50 rounded-2xl">
              <h3 className="text-xl font-bold text-gold-100 mb-4">📊 Deposit Summary</h3>
              <div className="grid grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gold-200">{deposits.length}</div>
                  <div className="text-gold-400 text-sm mt-1">Total Deposits</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">${totals.totalDeposits.toFixed(2)}</div>
                  <div className="text-gold-400 text-sm mt-1">Total Deposit Amount</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">${totals.totalBonus.toFixed(2)}</div>
                  <div className="text-gold-400 text-sm mt-1">Total Welcome Bonus</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">${totals.totalAddedToFund.toFixed(2)}</div>
                  <div className="text-gold-400 text-sm mt-1">Total Added to Fund</div>
                </div>
              </div>
              <div className="mt-4 text-center text-gold-300 text-sm">
                All deposits are automatically approved and added to your Fund Wallet immediately.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DepositHistory;
